import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, TrendingUp, LogIn, UserPlus } from 'lucide-react';

const Landing: React.FC = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Navigation */}
      <nav className="fixed w-full backdrop-blur-md bg-[#272727]/90 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <img src="/ElephantWhite copy copy.svg" alt="Spectacle" className="h-8 w-8 text-white" />
              <img src="/Spectacle typeface copy.svg" alt="Spectacle" className="ml-2 h-12 w-auto" />
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium flex items-center"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </Link>
              <Link
                to="/register"
                className="bg-purple-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-600 transition-colors flex items-center"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8">
            The Social Platform for
            <span className="text-purple-500"> Trusted News</span>
          </h1>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Connect with verified journalists, share reliable news, and engage in fact-based 
            discussions on the social platform dedicated to truth in journalism.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-500 hover:bg-purple-600 transition-colors"
          >
            Join Our Community
          </Link>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-[#272727] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Why Choose Spectacle?</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Our social platform is built to support quality journalism and foster unbiased fact-based discussions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#1a1a1a] p-6 rounded-xl">
              <Shield className="h-12 w-12 text-purple-500 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Verified Community</h3>
              <p className="text-gray-400">
                Connect and engage with authenticated journalists and credible news sources 
                in a trusted social environment.
              </p>
            </div>

            <div className="bg-[#1a1a1a] p-6 rounded-xl">
              <TrendingUp className="h-12 w-12 text-purple-500 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Fact Rating System</h3>
              <p className="text-gray-400">
                Our community-driven fact rating system helps identify and promote unbiased, 
                verified information.
              </p>
            </div>

            <div className="bg-[#1a1a1a] p-6 rounded-xl">
              <Users className="h-12 w-12 text-purple-500 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Social Engagement</h3>
              <p className="text-gray-400">
                Share, discuss, and connect with a community dedicated to truth and 
                meaningful discourse.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="bg-black py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">About Spectacle</h2>
            <p className="text-gray-300 mb-8">
              Spectacle is a social platform revolutionizing how we share and consume news. 
              In an era of misinformation and bias, we've created a space where verified 
              journalists and engaged readers come together to share, discuss, and evaluate 
              news with a commitment to truth.
            </p>
            <p className="text-gray-300">
              Our platform combines the power of social connectivity with rigorous fact-checking 
              and transparency. We empower journalists to share their stories while giving the 
              community tools to engage with and evaluate news content through an unbiased, 
              fact-based lens.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#272727] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center">
            <div className="flex items-center mb-6">
              <svg className="h-8 w-8 text-white" viewBox="0 0 902.4 956.58" fill="currentColor">
                <path d="M898.15,134.66c-16.65-48.66-89.26-85.33-89.26-85.33C665.12-25.34,602,6.94,602,6.94c-25.9,7.28-48.67,22.39-66.52,37.65h0c-22.71,18.91-64.14,63.84-64.14,63.84,7.07-12.9,13.69-25.3,22.14-37.36,1.42-1.98,2.89-3.98,4.34-5.97-35.87-3.62-70.22,.02-104.27,10.09-34.05,10.07-64.99,25.7-93.13,48.24,2.3,.88,4.61,1.76,6.89,2.65,13.66,5.53,26.08,12.32,39.02,19.3-.02,0-.03,0-.05-.01-1.62-.41-59.37-15.17-88.34-18.67-23.31-3.12-50.7-3.44-76.46,4.55,0,0-70.53,7.22-150.6,148.06,0,0-41,70.25-28.52,120.15,0,0,8.62,33.78,67.55,99.47,0,0,73.95,85.77,158.5,147.52,0,0,71.38,60.19,147.47,79.48,0,0,48.07,15.49,73.06-20.62v.02c.08-.11,.19-.28,.26-.39,.2-.29,.41-.56,.6-.85,0,0,5.59-8.5,12.42-21.23,4.99-8.89,10.29-18.57,14.44-26.21,1.32-2.49,2.67-4.96,4.03-7.44,.46-.84,.89-1.64,1.26-2.34v.04c4.57-8.23,9.34-16.37,14.41-24.37-1.35,5.24-2.79,10.45-4.32,15.63-6.3,21.35-11.05,43.14-13.53,65.27-1.34,11.97-2.48,23.95-2.25,35.96,.67,36.15,6.62,72.05,20.85,105.45,13.29,31.18,37.98,64.06,65.16,84.35,42.63,31.81,103.6,35.6,151.25,13.13,17.58-8.29,35.94-20.74,46.41-37.29,12.38-19.55,18.79-42.67,19.31-65.74,.46-20.4-3.56-40.76-10.84-59.78-6.96-18.19-21.25-33.14-25.2-52.48-2.45-12.02-.37-24.46,3.84-35.83v-.03c1.85-4.87-.29-10.34-4.95-12.67l-51.26-25.59c-4.57-2.28-10.18-.82-12.9,3.5-24,38.06-28.38,86.31-5.35,127.51,8.29,14.83,23.46,35.45,10.27,51.97-5.25,6.58-13.61,9.82-22.02,9.46-24.64-1.08-41.85-23.36-47.58-46.39-10.76-43.33,1.92-87.14,14.79-128.32,5.36-17.17,10.49-34.46,14.56-51.96-8.18-6.46-16.32-13.37-24.99-21.35,12.96,5.96,25.53,12.53,37.86,19.49,3.88,2.08,7.83,4.18,11.63,6.18,12.23,6.69,21.14,10.62,21.14,10.62,.17,.07,.33,.13,.5,.2,.27,.12,.63,.28,.87,.38v-.04c40.6,16.76,72.55-22.4,72.55-22.4,53.38-57.56,80.56-146.88,80.56-146.88,37.4-97.79,52.82-209.98,52.82-209.98,13.74-87.17,2.61-120.21,2.61-120.21Zm-265.86,306.51c-50.87,7.45-100.13-17.81-124.13-63.27-10.35-19.6-14.59-40.1-14.08-59.96-9.35-2.55-19.27-2.49-28.69,.3-9.42,2.79-17.78,8.13-24.24,15.35,11.23,16.39,18.81,35.89,20.78,57.96,4.58,51.2-23.02,99.18-69.76,120.6-72.35,33.15-155.24-10.11-170.28-87.15-11.33-58.05,21.78-116.5,77.32-136.82,41.08-15.03,84.35-6.39,116.07,18.13,10.77-11.24,24.29-19.62,39.45-24.1,15.16-4.48,31.07-4.8,46.22-1.23,13.29-37.82,44.9-68.6,87.55-78.32,57.66-13.15,117.23,17.9,139.28,72.78,29.27,72.83-16.77,154.2-95.5,165.73Z"/>
                <path d="M327.95,319.32c-54.42,8.85-85.64,66.26-63.48,116.75,15.27,34.78,52.17,54.85,89.67,48.75,54.43-8.85,85.64-66.26,63.48-116.75-15.27-34.78-52.17-54.85-89.67-48.75Z"/>
                <path d="M602.23,238.24c-54.43,8.85-85.64,66.26-63.48,116.75,15.27,34.78,52.17,54.85,89.67,48.75,54.43-8.85,85.64-66.26,63.47-116.75-15.27-34.78-52.17-54.85-89.67-48.75Z"/>
              </svg>
              <span className="ml-2 text-xl font-bold text-white">Spectacle</span>
            </div>
            <p className="text-gray-400 text-sm text-center">
              © {new Date().getFullYear()} Spectacle. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;